import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Feature {
  name: string;
  included: boolean;
}

const CreatePackagePage: React.FC = () => {
  const navigate = useNavigate();

  const [packageData, setPackageData] = useState({
    name: '',
    price: '',
    billingCycle: 'monthly',
    maxUsers: '',
    smsCredits: '',
    description: '',
  });

  const [features, setFeatures] = useState<Feature[]>([
    { name: '24/7 Support', included: false },
    { name: 'Analytics Dashboard', included: false },
    { name: 'API Access', included: false },
    { name: 'Custom Sender ID', included: false },
    { name: 'Delivery Reports', included: false },
    { name: 'Message Templates', included: false },
    { name: 'Scheduled Messages', included: false },
    { name: 'Bulk SMS', included: false },
  ]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setPackageData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFeatureToggle = (index: number) => {
    const updatedFeatures = [...features];
    updatedFeatures[index].included = !updatedFeatures[index].included;
    setFeatures(updatedFeatures);
  };

  const handleSavePackage = () => {
    // Implement save functionality
    console.log('Saving package...', { packageData, features });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-medium">Create New Package</h1>
        <div className="space-x-3">
          <button
            onClick={() => navigate('/superadmin/billing')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSavePackage}
            className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
          >
            Save Package
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* Basic Package Information */}
        <div>
          <h2 className="text-lg font-medium mb-4">Package Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package Name
              </label>
              <input
                type="text"
                name="name"
                value={packageData.name}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="e.g., Enterprise"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price
              </label>
              <input
                type="text"
                name="price"
                value={packageData.price}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="e.g., 299"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Billing Cycle
              </label>
              <select
                name="billingCycle"
                value={packageData.billingCycle}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annually">Annually</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Users
              </label>
              <input
                type="number"
                name="maxUsers"
                value={packageData.maxUsers}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="e.g., 50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMS Credits
              </label>
              <input
                type="number"
                name="smsCredits"
                value={packageData.smsCredits}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="e.g., 10000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={packageData.description}
                onChange={handleInputChange}
                rows={2}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Brief package description..."
              />
            </div>
          </div>
        </div>

        {/* Features */}
        <div>
          <h2 className="text-lg font-medium mb-4">Package Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <div 
                key={feature.name}
                className="flex items-center space-x-3 p-3 border rounded-md hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={feature.included}
                  onChange={() => handleFeatureToggle(index)}
                  className="h-4 w-4 text-[#FF5722] rounded border-gray-300 focus:ring-[#FF5722]"
                />
                <label className="text-sm text-gray-700">{feature.name}</label>
              </div>
            ))}
          </div>
        </div>

        {/* Package Notes */}
        <div className="border-t pt-6">
          <h2 className="text-lg font-medium mb-4">Additional Information</h2>
          <div className="space-y-2 text-sm text-gray-600">
            <p>• Package prices are subject to change without prior notice</p>
            <p>• Additional carrier fees may apply for SMS services</p>
            <p>• Support levels vary based on package selection</p>
            <p>• Custom features can be added upon request</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePackagePage; 